/**
 * Outbox v2 storage — NATIVE engine (expo-file-system). Contract: `outboxStore.ts`.
 *
 * Layout: `documentDirectory/atlas-outbox-v2/<orgFirestoreId>/<seq:012>-<entryId>.json`, one JSON
 * file per entry. The zero-padded seq makes a plain name sort equal the queue order and survives
 * any number of entries a device can plausibly hold (10^12).
 *
 * WHY expo-file-system: it is already in the shipped binary (`expo-file-system` 18.1.11, used by
 * `utils/exportUtils.js`), so this ships as an OTA on runtimeVersion 5.0. It uses only the
 * long-standing module API (`writeAsStringAsync`, `moveAsync`, `readDirectoryAsync`,
 * `getFreeDiskStorageAsync`, …), not the SDK 53 `expo-file-system/next` API.
 *
 * ATOMICITY, and why the obvious recipe is not enough on iOS. Every write goes to
 * `<name>.json.tmp` first and is then published with `moveAsync` onto `<name>.json`.
 *  - Android's `moveAsync` is `File.renameTo`, i.e. rename(2): an atomic replace. A kill leaves
 *    either the old file or the new one.
 *  - iOS's `moveAsync` (expo-file-system 18.1.11, `ios/FileSystemModule.swift:89-103`) REMOVES the
 *    destination and then calls `moveItem`. A kill between the two leaves NO `<name>.json` — only
 *    the complete `<name>.json.tmp`. Deleting stray `.tmp` files at open (the textbook cleanup)
 *    would therefore delete the only copy of the entry.
 * So recovery — which runs the first time each organization is touched after a (re)start, before
 * any read or write of it — never deletes a `.tmp` blindly. For each one:
 *  - its `.json` exists and is valid     → the `.tmp` is an unpublished update; delete the `.tmp`.
 *  - its `.json` is missing or invalid   → if the `.tmp` is valid, PUBLISH it (promote); a missing
 *    `.json` is either an append that never published or the iOS gap above, and both mean the
 *    `.tmp` holds the only complete copy.
 *  - the `.tmp` is invalid too           → leave both files untouched and report the slot corrupt.
 * iOS's `writeAsStringAsync` writes atomically itself (`atomically: true`); Android's truncates
 * and writes in place, which is why a truncated `.tmp` is possible there and handled above.
 *
 * A FAILED APPEND MUST NOT COME BACK (review S1). Because recovery promotes a lone valid `.tmp`, an
 * append whose publish failed and whose `.tmp` survived would reappear after the next restart —
 * after the caller had been told it failed and may have re-rung the sale. So a failed append
 * removes its `.tmp` and then VERIFIES that neither the `.tmp` nor a published file exists. If
 * that cannot be proven, it rejects with `OutboxAppendIndeterminateError` (carrying the entryId),
 * never with the plain error.
 *
 * INSTALL ID (review 1 S3, review 2 item 4, review 3 A). `installId = digest(vendorId + '|' +
 * installationTime)`, both from `expo-application` 6.1.5 (in the binary: a dependency since the
 * monorepo commit, pinned 6.1.5 before runtime 5.0, also required by `expo-notifications`):
 *  - vendor id: `getIosIdForVendorAsync()` (IDFV) on iOS, `getAndroidId()` (ANDROID_ID) on Android.
 *  - installation time: `getInstallationTimeAsync()`. Verified in the 6.1.5 native code: Android
 *    returns `PackageInfo.firstInstallTime` (`ApplicationModule.kt:40-47`); iOS returns the creation
 *    date of the app's Documents directory (`ApplicationModule.swift:22-30`).
 * WHY BOTH. ANDROID_ID alone missed the case that matters most on Android: uninstall + reinstall on
 * the SAME device restores `documentDirectory` (= filesDir) through Auto Backup (`allowBackup`
 * defaults to true), ANDROID_ID is unchanged, and the restored stale entries would read as this
 * install's own. `firstInstallTime` is new on every reinstall, so the id changes.
 *
 * When each platform yields a NEW id (so entries restored with the queue read `foreignInstall`).
 * [code] = verified in the expo-application 6.1.5 source; [device] = needs the device check.
 *  - App update, same install: SAME id. Android: firstInstallTime survives updates [code: the value
 *    read; platform semantics: device]. iOS: the Documents directory is kept [device].
 *  - Uninstall + reinstall with the backup restored: NEW id. Android: new firstInstallTime, while
 *    Auto Backup brings the queue back [device]. iOS: the restored Documents directory is created
 *    anew, so its creation date is new [device].
 *  - Restore onto ANOTHER device: NEW id — IDFV / ANDROID_ID differ, and so does the time [device].
 *  - Factory reset / erase + restore onto the SAME device: Android: NEW id, ANDROID_ID resets
 *    [device]. iOS: POSSIBLY UNDETECTED — a backup may restore the Documents directory with its
 *    ORIGINAL creation date while the IDFV survives, which reproduces the old id and lets restored,
 *    possibly delivered entries read as this install's own. The device check (stage-0 §4.4) is
 *    expected to possibly fail here; the real fix is the native-build follow-up below that keeps
 *    the queue out of backups altogether.
 *  - iOS, every app of this vendor deleted: NEW id (IDFV changes; Apple-documented).
 *  - Android signing-key rotation: NEW id (ANDROID_ID is per signing key), so everything queued at
 *    that moment is held for an operator — the safe side.
 * The code path (which values feed the digest) is verified here; every platform behaviour in the
 * table is inferred until the stage-3 native device protocol confirms it
 * (`docs/technical/write-cutover-stage0-baseline.md` §4.4).
 *
 * The digest is not a secret and not an anonymiser: it is a non-reversible digest in practice of
 * a device identifier that is itself scoped to this app's signing key (Android) or vendor (iOS).
 * It keeps the raw vendor id out of P3's telemetry; it does not make `installId` unlinkable.
 *
 * FALLBACK: a file in `cacheDirectory`, never beside the queue (an id restored WITH the entries
 * could not tell a restored queue from a live one). IDFV can be nil after a reboot until the device
 * is first unlocked; for such a launch the cached copy of the last derived id is used (`cache-read`).
 * With the derived id available the cache is refreshed BEST-EFFORT: a failing cache read or write
 * never fails an operation (review 3 C). Only a freshly minted id (`cache-created`, neither source
 * available) needs a durable write, because the entries it stamps must keep reading as this
 * install's. A `cache-created` launch errs on the safe side twice: its older entries read foreign,
 * and the entries IT stamps read foreign again on the next launch that has the derived id.
 *
 * STRONGER FIX, NATIVE-BUILD FOLLOW-UP (out of scope for this OTA-only stage): exclude the queue
 * directory from Android backup (`android:allowBackup` / `fullBackupContent` /
 * `dataExtractionRules` excluding `files/atlas-outbox-v2`) and mark it excluded from iCloud backup
 * — both need native configuration, so a store build.
 *
 * ONE INSTANCE PER DIRECTORY. `seq` is `max(existing) + 1`, kept in memory, and every operation is
 * serialised through one in-process chain (React Native has a single JS context). Two instances on
 * the same directory would mint the same seq; `openOutboxStore` therefore returns one shared
 * instance, and after `close()` a new one whose operations wait until the old one has drained and
 * closed (`createSharedStoreOpener`). `createNativeOutboxStore` exists for tests, which open a
 * second instance only after abandoning the first to simulate a restart.
 *
 * The directory is the source of truth; the in-memory index is a cache of it. Any mutation that
 * fails part-way discards the organization's cache, so the next operation re-reads the directory
 * and runs recovery rather than trusting a half-applied cache.
 */
import * as ExpoApplication from 'expo-application'
import * as ExpoFileSystem from 'expo-file-system'
import { Platform } from 'react-native'

import {
  applyEntryPatch,
  isStorageSafeId,
  prepareNewEntry,
  validateStoredEntry,
  type NewOutboxEntry,
  type OutboxEntryPatch,
  type OutboxEntryV2,
} from './outboxEntry'
import {
  checkAdoptionAudit,
  compareSlots,
  createSharedStoreOpener,
  generateInstallId,
  OutboxAdoptionRefusedError,
  OutboxAppendIndeterminateError,
  OutboxCorruptEntryError,
  OutboxDuplicateEntryError,
  OutboxValidationError,
  planDeletePurge,
  type AdoptForeignResult,
  type AppendDeleteResult,
  type InstallIdOrigin,
  type OutboxCensus,
  type OutboxCorruptSlot,
  type OutboxSlot,
  type OutboxStore,
  type OutboxStraySlot,
} from './outboxStore'

/** The subset of expo-file-system this engine uses — injectable so tests can crash it. */
export type OutboxFileSystem = Pick<
  typeof ExpoFileSystem,
  | 'getInfoAsync'
  | 'readAsStringAsync'
  | 'writeAsStringAsync'
  | 'deleteAsync'
  | 'moveAsync'
  | 'makeDirectoryAsync'
  | 'readDirectoryAsync'
  | 'getFreeDiskStorageAsync'
>

export const OUTBOX_ROOT_DIR_NAME = 'atlas-outbox-v2'
export const OUTBOX_INSTALL_ID_FILE_NAME = 'atlas-outbox-v2-install-id'
/**
 * The Firestore-mode MIRROR-INTENT journal (ATL-1459, stage 3 P7; `global/Firebase/mirrorIntent.ts`):
 * the same engine in its own directory, so the v2 master queue (P3) can never see — let alone send —
 * a mirror whose Firestore commit may not have happened. Its own install-id cache file for the same
 * reason every namespace needs one: two stores minting a fallback id concurrently must not race.
 */
export const MIRROR_INTENT_ROOT_DIR_NAME = 'atlas-mirror-intent-v1'
export const MIRROR_INTENT_INSTALL_ID_FILE_NAME = 'atlas-mirror-intent-v1-install-id'
/**
 * The QUARANTINE of v1 absolute stock mirrors (ATL-1445 stage 3, P8 review 1, S-3; `outboxQuarantine.ts`):
 * the same engine in its own directory, OUTSIDE the v2 drain queue. An entry here is never sent; it waits
 * for an operator to upload or discard it, and no other write ever waits behind it.
 */
export const QUARANTINE_ROOT_DIR_NAME = 'atlas-outbox-v2-quarantine'
export const QUARANTINE_INSTALL_ID_FILE_NAME = 'atlas-outbox-v2-quarantine-install-id'
const SEQ_DIGITS = 12
const MAX_SEQ = 10 ** SEQ_DIGITS - 1
const TMP_SUFFIX = '.tmp'
const ENTRY_FILE_PATTERN = /^(\d{12})-([A-Za-z0-9_-]{1,128})\.json$/

/**
 * Below this the device itself is close to full — Firestore's own cache and the OS start failing
 * writes. The queue needs far less (an entry is kilobytes), so this is a warning about the
 * device, raised before the queue can be the thing that fails.
 */
export const LOW_DISK_BYTES = 100 * 1024 * 1024

export function entryFileName(seq: number, entryId: string): string {
  return `${String(seq).padStart(SEQ_DIGITS, '0')}-${entryId}.json`
}

// ============================================================================
// Engine
// ============================================================================

type OkRecord = { status: 'ok'; name: string; entry: OutboxEntryV2 }
type CorruptRecord = { status: 'corrupt'; name: string; slot: OutboxCorruptSlot; extraFiles: string[] }
type StrayRecord = { status: 'stray'; name: string; slot: OutboxStraySlot }
type FileRecord = OkRecord | CorruptRecord | StrayRecord

interface OrgState {
  dir: string
  dirExists: boolean
  maxSeq: number
  /**
   * Every record in queue order, corrupt and stray ones included. Lookups by entryId scan it and
   * take the FIRST match, which is the entry of record when a later duplicate exists (see
   * `loadOrg`), and which makes a corrupt record carrying that id block a new append of it.
   */
  records: FileRecord[]
}

function entryIdOf(record: FileRecord): string | null {
  if (record.status === 'ok') return record.entry.entryId
  return record.slot.entryId
}

function seqOf(record: FileRecord): number | null {
  return record.status === 'ok' ? record.entry.seq : record.slot.seq
}

function findRecord(state: OrgState, entryId: string): FileRecord | undefined {
  return state.records.find((record) => entryIdOf(record) === entryId)
}

type ParseResult = { ok: true; entry: OutboxEntryV2 } | { ok: false; reason: string }

function parseEntryFile(orgFirestoreId: string, fileName: string, raw: string): ParseResult {
  const match = ENTRY_FILE_PATTERN.exec(fileName)
  if (!match) return { ok: false, reason: 'unrecognized file name' }
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return { ok: false, reason: raw.length === 0 ? 'empty file' : 'not valid JSON' }
  }
  const validated = validateStoredEntry(parsed)
  if (!validated.ok) return { ok: false, reason: validated.reason }
  const entry = validated.value
  if (entry.seq !== Number(match[1])) return { ok: false, reason: 'seq disagrees with the file name' }
  if (entry.entryId !== match[2]) return { ok: false, reason: 'entryId disagrees with the file name' }
  if (entry.orgFirestoreId !== orgFirestoreId) {
    return { ok: false, reason: 'orgFirestoreId disagrees with the directory' }
  }
  return { ok: true, entry }
}

function cloneEntry(entry: OutboxEntryV2): OutboxEntryV2 {
  return JSON.parse(JSON.stringify(entry)) as OutboxEntryV2
}

function assertOrgId(orgFirestoreId: string): void {
  if (!isStorageSafeId(orgFirestoreId)) {
    throw new OutboxValidationError('orgFirestoreId: not a storage-safe id')
  }
}

function parentUri(fileUri: string): string {
  return fileUri.slice(0, fileUri.lastIndexOf('/') + 1)
}

export interface NativeOutboxStoreOptions {
  fs: OutboxFileSystem
  /** Directory URI ending in `/`, e.g. `${documentDirectory}atlas-outbox-v2/`. */
  rootUri: string
  /** File URI of the fallback install id, OUTSIDE `rootUri` and outside the backup set (see INSTALL ID). */
  installIdUri: string
  /** The OS vendor id, or `null` when unavailable this launch (see INSTALL ID). */
  vendorId: () => Promise<string | null>
  /** The app's installation time in epoch ms, or `null` when unavailable (see INSTALL ID). */
  installationTime: () => Promise<number | null>
  /** Injectable for tests. */
  generateId?: () => string
  /** Injectable for tests. */
  now?: () => number
}

/**
 * `i1-` + a 64-bit FNV-1a digest (two 32-bit lanes) of `vendorId|installationTimeMs`.
 *
 * PERSISTED FORMAT: every queued entry carries this value, and an entry whose `installId` stops
 * matching reads `foreignInstall` and is HELD. Changing the prefix, the seeds, the primes, the lane
 * mixing or the input layout would therefore hold every device's whole queue at the next launch.
 * Never change it without a versioned migration (a new prefix, with the old derivation still
 * recognised). The golden values in `outboxStore.native.test.js` pin it.
 */
export function installIdFromIdentity(vendorId: string, installationTimeMs: number): string {
  const input = `${vendorId}|${Math.trunc(installationTimeMs)}`
  let a = 0x811c9dc5
  let b = 0xcbf29ce4
  for (let i = 0; i < input.length; i++) {
    const c = input.charCodeAt(i)
    a = Math.imul(a ^ c, 0x01000193) >>> 0
    b = Math.imul(b ^ c ^ (i & 0xff), 0x01000193) >>> 0
  }
  return `i1-${a.toString(16).padStart(8, '0')}${b.toString(16).padStart(8, '0')}`
}

/** IDFV / ANDROID_ID through expo-application; `null` when the OS does not give one. Never throws. */
export async function platformVendorId(): Promise<string | null> {
  try {
    if (Platform.OS === 'ios') return await ExpoApplication.getIosIdForVendorAsync()
    if (Platform.OS === 'android') return ExpoApplication.getAndroidId()
    return null
  } catch {
    return null
  }
}

/** `getInstallationTimeAsync()` in epoch ms; `null` when unavailable or not a real time. Never throws. */
export async function platformInstallationTime(): Promise<number | null> {
  try {
    if (Platform.OS !== 'ios' && Platform.OS !== 'android') return null
    const time = (await ExpoApplication.getInstallationTimeAsync()).getTime()
    return Number.isFinite(time) && time > 0 ? time : null
  } catch {
    return null
  }
}

export function createNativeOutboxStore({
  fs,
  rootUri,
  installIdUri,
  vendorId,
  installationTime,
  generateId = generateInstallId,
  now = Date.now,
}: NativeOutboxStoreOptions): OutboxStore {
  if (!rootUri.endsWith('/')) throw new Error('outbox rootUri must end with "/"')
  if (installIdUri.startsWith(rootUri)) throw new Error('outbox installIdUri must live outside rootUri')
  const orgs = new Map<string, OrgState>()
  let install: { id: string; origin: InstallIdOrigin } | null = null
  let chain: Promise<unknown> = Promise.resolve()
  let closed = false
  let closing: Promise<void> | null = null

  function serialize<T>(op: () => Promise<T>, needsInstallId = true): Promise<T> {
    const run = chain.then(async () => {
      if (closed) throw new Error('outbox store is closed')
      if (needsInstallId) await ensureInstallId()
      return op()
    })
    chain = run.catch(() => undefined)
    return run
  }

  /**
   * Resolve the install id (see INSTALL ID). With the derived id available, the cache file is only
   * refreshed, best-effort: nothing about it can fail the operation. Without it, a READ ERROR on
   * the cache file is not "none": it propagates, because minting on a transient failure would turn
   * every queued entry foreign; and a minted id is written durably before anything is stamped.
   */
  async function ensureInstallId(): Promise<string> {
    if (install) return install.id
    const [vendor, installedAt] = await Promise.all([
      vendorId().catch(() => null),
      installationTime().catch(() => null),
    ])
    if (vendor && installedAt !== null && Number.isFinite(installedAt) && installedAt > 0) {
      const id = installIdFromIdentity(vendor, installedAt)
      try {
        const info = await fs.getInfoAsync(installIdUri)
        const cached = info.exists ? (await fs.readAsStringAsync(installIdUri)).trim() : null
        if (cached !== id) await writeInstallIdFile(id)
      } catch {
        // Best-effort (review 3 C): the derived id does not depend on the cache.
      }
      install = { id, origin: 'vendor+install' }
      return id
    }
    const info = await fs.getInfoAsync(installIdUri)
    const cached = info.exists ? (await fs.readAsStringAsync(installIdUri)).trim() : null
    if (cached !== null && isStorageSafeId(cached)) {
      install = { id: cached, origin: 'cache-read' }
      return cached
    }
    const minted = generateId()
    if (!isStorageSafeId(minted)) throw new Error('install id is not storage-safe')
    await writeInstallIdFile(minted)
    install = { id: minted, origin: 'cache-created' }
    return minted
  }

  async function writeInstallIdFile(id: string): Promise<void> {
    await fs.makeDirectoryAsync(parentUri(installIdUri), { intermediates: true })
    await fs.writeAsStringAsync(installIdUri + TMP_SUFFIX, id)
    await fs.moveAsync({ from: installIdUri + TMP_SUFFIX, to: installIdUri })
  }

  function currentInstallId(): string {
    if (!install) throw new Error('outbox install id not loaded')
    return install.id
  }

  async function readIfFile(uri: string): Promise<string | null> {
    const info = await fs.getInfoAsync(uri)
    if (!info.exists || info.isDirectory) return null
    return fs.readAsStringAsync(uri)
  }

  function corruptRecord(
    name: string,
    reason: string,
    corruption: string,
    raw: string | null,
    extraFiles: string[] = [],
  ): CorruptRecord {
    const match = ENTRY_FILE_PATTERN.exec(name.endsWith(TMP_SUFFIX) ? name.slice(0, -TMP_SUFFIX.length) : name)
    return {
      status: 'corrupt',
      name,
      extraFiles,
      slot: {
        status: 'corrupt',
        seq: match ? Number(match[1]) : null,
        entryId: match ? match[2] : null,
        ref: name,
        reason: `${corruption}: ${reason}`,
        raw,
      },
    }
  }

  function strayRecord(name: string, reason: string, raw: string | null): StrayRecord {
    return { status: 'stray', name, slot: { status: 'stray', seq: null, entryId: null, ref: name, reason, raw } }
  }

  /** Read one organization's directory, run crash recovery, and build its index. */
  async function loadOrg(orgFirestoreId: string): Promise<OrgState> {
    const cached = orgs.get(orgFirestoreId)
    if (cached) return cached

    const dir = `${rootUri}${orgFirestoreId}/`
    const state: OrgState = { dir, dirExists: false, maxSeq: 0, records: [] }
    const info = await fs.getInfoAsync(dir)
    if (info.exists) {
      state.dirExists = true
      const names = (await fs.readDirectoryAsync(dir)).slice().sort()
      const finals = new Set(names.filter((n) => ENTRY_FILE_PATTERN.test(n)))
      const tmps = names.filter((n) => n.endsWith(TMP_SUFFIX) && ENTRY_FILE_PATTERN.test(n.slice(0, -TMP_SUFFIX.length)))
      const tmpSet = new Set(tmps)
      const others = names.filter((n) => !finals.has(n) && !tmpSet.has(n))
      const resolved = new Map<string, FileRecord>()

      for (const tmpName of tmps) {
        const finalName = tmpName.slice(0, -TMP_SUFFIX.length)
        const tmpRaw = await fs.readAsStringAsync(dir + tmpName)
        const tmpParsed = parseEntryFile(orgFirestoreId, finalName, tmpRaw)
        if (finals.has(finalName)) {
          const finalRaw = await fs.readAsStringAsync(dir + finalName)
          const finalParsed = parseEntryFile(orgFirestoreId, finalName, finalRaw)
          if (finalParsed.ok) {
            // An update whose publish never happened: the published version stands.
            await fs.deleteAsync(dir + tmpName, { idempotent: true })
            resolved.set(finalName, { status: 'ok', name: finalName, entry: finalParsed.entry })
          } else if (tmpParsed.ok) {
            // The published file is damaged but its replacement is whole: publish the replacement.
            await fs.moveAsync({ from: dir + tmpName, to: dir + finalName })
            resolved.set(finalName, { status: 'ok', name: finalName, entry: tmpParsed.entry })
          } else {
            resolved.set(
              finalName,
              corruptRecord(
                finalName,
                `${finalParsed.reason}; its pending replacement is unreadable too (${tmpParsed.reason})`,
                'unreadable',
                finalRaw,
                [tmpName],
              ),
            )
          }
        } else if (tmpParsed.ok) {
          // An append that never published, or iOS's remove-then-move gap: the only complete copy.
          await fs.moveAsync({ from: dir + tmpName, to: dir + finalName })
          finals.add(finalName)
          resolved.set(finalName, { status: 'ok', name: finalName, entry: tmpParsed.entry })
        } else {
          resolved.set(finalName, corruptRecord(tmpName, tmpParsed.reason, 'incomplete-write', tmpRaw))
        }
      }

      for (const finalName of finals) {
        if (resolved.has(finalName)) continue
        const raw = await fs.readAsStringAsync(dir + finalName)
        const parsed = parseEntryFile(orgFirestoreId, finalName, raw)
        resolved.set(
          finalName,
          parsed.ok
            ? { status: 'ok', name: finalName, entry: parsed.entry }
            : corruptRecord(finalName, parsed.reason, 'unreadable', raw),
        )
      }

      for (const other of others) {
        // Not a name this engine ever writes (e.g. an iOS atomic-write leftover `.dat.nosync…`).
        // Surfaced with its bytes, never deleted: only an operator decides it is not a write.
        resolved.set(other, strayRecord(other, 'not a file name this store writes', await readIfFile(dir + other)))
      }

      const records = [...resolved.values()].sort((a, b) => compareSlots({ seq: seqOf(a) }, { seq: seqOf(b) }))
      const seen = new Set<string>()
      for (const record of records) {
        const seq = seqOf(record)
        if (seq !== null) state.maxSeq = Math.max(state.maxSeq, seq)
        const entryId = entryIdOf(record)
        if (entryId === null) {
          state.records.push(record)
          continue
        }
        if (seen.has(entryId)) {
          // Two files for one entry: the earlier stands, the later is surfaced, neither is deleted.
          const raw = record.status === 'ok' ? JSON.stringify(record.entry) : record.slot.raw
          const duplicate = corruptRecord(
            record.name,
            `a second record for entry ${entryId}`,
            'duplicate',
            raw,
            record.status === 'corrupt' ? record.extraFiles : [],
          )
          state.records.push(duplicate)
          continue
        }
        seen.add(entryId)
        state.records.push(record)
      }
    }
    orgs.set(orgFirestoreId, state)
    return state
  }

  function slotOf(record: FileRecord): OutboxSlot {
    if (record.status !== 'ok') return { ...record.slot }
    return {
      status: 'ok',
      seq: record.entry.seq,
      entry: cloneEntry(record.entry),
      foreignInstall: record.entry.installId !== currentInstallId(),
    }
  }

  function replaceRecord(state: OrgState, previous: FileRecord | null, next: FileRecord | null): void {
    if (previous) state.records = state.records.filter((r) => r !== previous)
    if (next) {
      state.records.push(next)
      state.records.sort((a, b) => compareSlots({ seq: seqOf(a) }, { seq: seqOf(b) }))
    }
  }

  /** Write `<name>.tmp`, then publish it onto `<name>`. */
  async function writeRecord(dir: string, name: string, entry: OutboxEntryV2): Promise<void> {
    await fs.writeAsStringAsync(dir + name + TMP_SUFFIX, JSON.stringify(entry))
    await fs.moveAsync({ from: dir + name + TMP_SUFFIX, to: dir + name })
  }

  /** After a failed append: `true` only when NO copy of it can come back (review S1). */
  async function provedAbsent(dir: string, name: string): Promise<boolean> {
    try {
      await fs.deleteAsync(dir + name + TMP_SUFFIX, { idempotent: true })
      const [tmp, published] = await Promise.all([
        fs.getInfoAsync(dir + name + TMP_SUFFIX),
        fs.getInfoAsync(dir + name),
      ])
      return !tmp.exists && !published.exists
    } catch {
      return false
    }
  }

  async function appendLocked(input: NewOutboxEntry): Promise<OutboxEntryV2> {
    const prepared = prepareNewEntry(input)
    if (!prepared.ok) throw new OutboxValidationError(prepared.reason)
    const org = prepared.value.orgFirestoreId
    const state = await loadOrg(org)
    const existing = findRecord(state, prepared.value.entryId)
    if (existing) {
      throw new OutboxDuplicateEntryError(org, prepared.value.entryId, existing.status === 'ok' ? 'ok' : 'corrupt')
    }
    const seq = state.maxSeq + 1
    if (seq > MAX_SEQ) throw new Error('outbox seq space exhausted')
    const entry: OutboxEntryV2 = { ...prepared.value, seq, installId: currentInstallId(), adoptions: [] }
    const name = entryFileName(seq, entry.entryId)
    try {
      if (!state.dirExists) {
        await fs.makeDirectoryAsync(state.dir, { intermediates: true })
        state.dirExists = true
      }
      await writeRecord(state.dir, name, entry)
    } catch (error) {
      // The directory, not this half-applied cache, is what the next call must see.
      orgs.delete(org)
      // A plain rejection promises "nothing was queued"; recovery would promote a surviving `.tmp`
      // (and a move that failed AFTER renaming leaves a published file). Only a proven absence
      // earns the plain rejection.
      if (await provedAbsent(state.dir, name)) throw error
      throw new OutboxAppendIndeterminateError(org, entry.entryId, error)
    }
    state.maxSeq = seq
    replaceRecord(state, null, { status: 'ok', name, entry })
    return cloneEntry(entry)
  }

  function requireOk(org: string, entryId: string, record: FileRecord): OkRecord {
    if (record.status !== 'ok') throw new OutboxCorruptEntryError(org, entryId, record.slot.reason)
    return record
  }

  const store: OutboxStore = {
    engine: 'file',

    append(entry) {
      return serialize(() => appendLocked(entry))
    },

    appendDelete(input) {
      return serialize(async (): Promise<AppendDeleteResult> => {
        if ((input as { kind?: unknown } | null)?.kind !== 'delete') {
          throw new OutboxValidationError('appendDelete: kind must be "delete"')
        }
        // Publish the delete FIRST. A crash before the purge below then leaves the superseded
        // entries in front of it — replaying them and then the delete is still correct. The other
        // order could purge an update and crash before the delete that justified it was queued.
        const entry = await appendLocked(input)
        const state = await loadOrg(entry.orgFirestoreId)
        const others = state.records.filter((r) => !(r.status === 'ok' && r.entry.entryId === entry.entryId))
        const plan = planDeletePurge(entry, others.map(slotOf))
        const doomed = new Set(plan.purge.map((e) => e.entryId))
        // LATEST FIRST: whatever a failure or a kill leaves is a seq-order prefix — the create and
        // its earliest updates — never an update whose create is already gone.
        const targets = others
          .filter((r): r is OkRecord => r.status === 'ok' && doomed.has(r.entry.entryId))
          .sort((x, y) => y.entry.seq - x.entry.seq)
        const purged: OutboxEntryV2[] = []
        for (const record of targets) {
          try {
            await fs.deleteAsync(state.dir + record.name, { idempotent: true })
          } catch {
            // The delete is already queued; what is left in front of it is safe to drain. Report
            // it rather than reject — a rejection would tell the caller the delete was NOT queued.
            orgs.delete(entry.orgFirestoreId)
            return { entry, purged, purgeWithheld: null, purgeIncomplete: true }
          }
          replaceRecord(state, record, null)
          purged.push(cloneEntry(record.entry))
        }
        return { entry, purged, purgeWithheld: plan.withheld, purgeIncomplete: false }
      })
    },

    list(orgFirestoreId) {
      return serialize(async () => {
        assertOrgId(orgFirestoreId)
        const state = await loadOrg(orgFirestoreId)
        return state.records.map(slotOf)
      })
    },

    get(orgFirestoreId, entryId) {
      return serialize(async () => {
        assertOrgId(orgFirestoreId)
        const state = await loadOrg(orgFirestoreId)
        const record = findRecord(state, entryId)
        if (!record) return null
        return cloneEntry(requireOk(orgFirestoreId, entryId, record).entry)
      })
    },

    update(orgFirestoreId, entryId, patch: OutboxEntryPatch) {
      return serialize(async () => {
        assertOrgId(orgFirestoreId)
        const state = await loadOrg(orgFirestoreId)
        const record = findRecord(state, entryId)
        if (!record) return null // never resurrect a removed entry
        const current = requireOk(orgFirestoreId, entryId, record)
        const merged = applyEntryPatch(current.entry, patch)
        if (!merged.ok) throw new OutboxValidationError(merged.reason)
        try {
          await writeRecord(state.dir, current.name, merged.value)
        } catch (error) {
          // Never delete the `.tmp` here: on iOS a failed publish may already have removed the
          // `.json`, leaving the `.tmp` as the only copy. Recovery at the next load decides.
          orgs.delete(orgFirestoreId)
          throw error
        }
        replaceRecord(state, current, { status: 'ok', name: current.name, entry: merged.value })
        return cloneEntry(merged.value)
      })
    },

    remove(orgFirestoreId, entryId) {
      return serialize(async () => {
        assertOrgId(orgFirestoreId)
        const state = await loadOrg(orgFirestoreId)
        const record = findRecord(state, entryId)
        if (!record) return false
        const current = requireOk(orgFirestoreId, entryId, record)
        try {
          await fs.deleteAsync(state.dir + current.name, { idempotent: true })
        } catch (error) {
          orgs.delete(orgFirestoreId)
          throw error
        }
        replaceRecord(state, current, null)
        return true
      })
    },

    removeCorrupt(orgFirestoreId, ref) {
      return serialize(async () => {
        if (ref.includes('/') || ref === '.' || ref === '..' || ref.length === 0) {
          throw new OutboxValidationError('removeCorrupt: ref is not a file name')
        }
        if (orgFirestoreId === null) {
          const census = await censusLocked()
          if (!census.unattributed.some((slot) => slot.ref === ref)) return false
          await fs.deleteAsync(rootUri + ref, { idempotent: true })
          return true
        }
        assertOrgId(orgFirestoreId)
        const state = await loadOrg(orgFirestoreId)
        const record = state.records.find((r) => r.status !== 'ok' && r.slot.ref === ref)
        if (!record || record.status === 'ok') return false
        try {
          await fs.deleteAsync(state.dir + record.name, { idempotent: true })
          if (record.status === 'corrupt') {
            for (const extra of record.extraFiles) await fs.deleteAsync(state.dir + extra, { idempotent: true })
          }
        } catch (error) {
          orgs.delete(orgFirestoreId)
          throw error
        }
        orgs.delete(orgFirestoreId) // re-read: a duplicate's twin may now be the entry of record
        return true
      })
    },

    adoptForeign(orgFirestoreId, entryIds, audit) {
      return serialize(async (): Promise<AdoptForeignResult> => {
        assertOrgId(orgFirestoreId)
        const checkedAudit = checkAdoptionAudit(audit)
        if (install && install.origin !== 'vendor+install') {
          // Review 4: under ANY cache-derived id — minted this launch (`cache-created`) or read back
          // from the cache (`cache-read`, which may itself be an earlier minted id) — entries
          // re-stamped now read foreign again at the next launch that has the derived id, so the
          // adoption would be silently wasted. Only the derived id can adopt.
          throw new OutboxAdoptionRefusedError(
            install.origin === 'cache-created' ? 'cache-created' : 'cache-read',
            `the install id is cache-derived (${install.origin}); adopt on a launch with the derived id`,
          )
        }
        const current = currentInstallId()
        const result: AdoptForeignResult = { adopted: [], notForeign: [], notFound: [], corrupt: [] }
        for (const entryId of entryIds) {
          const state = await loadOrg(orgFirestoreId)
          const record = findRecord(state, entryId)
          if (!record) {
            result.notFound.push(entryId)
            continue
          }
          if (record.status !== 'ok') {
            result.corrupt.push(entryId)
            continue
          }
          if (record.entry.installId === current) {
            result.notForeign.push(entryId)
            continue
          }
          const adopted: OutboxEntryV2 = {
            ...record.entry,
            installId: current,
            adoptions: [
              ...record.entry.adoptions,
              { fromInstallId: record.entry.installId, at: now(), ...checkedAudit },
            ],
          }
          const checked = validateStoredEntry(JSON.parse(JSON.stringify(adopted)))
          if (!checked.ok) throw new OutboxValidationError(checked.reason)
          try {
            await writeRecord(state.dir, record.name, checked.value)
          } catch (error) {
            orgs.delete(orgFirestoreId)
            throw error
          }
          replaceRecord(state, record, { status: 'ok', name: record.name, entry: checked.value })
          result.adopted.push(entryId)
        }
        return result
      })
    },

    census() {
      return serialize(censusLocked)
    },

    close() {
      // Idempotent (review 3 B): a repeat call returns the same promise.
      if (!closing) {
        closing = serialize(async () => {
          closed = true
          orgs.clear()
        }, false)
      }
      return closing
    },
  }

  async function censusLocked(): Promise<OutboxCensus> {
    const installId = currentInstallId()
    const census: OutboxCensus = {
      installId,
      installIdOrigin: install?.origin ?? 'existing',
      organizations: [],
      unattributed: [],
      foreignInstallEntries: [],
    }
    const info = await fs.getInfoAsync(rootUri)
    if (!info.exists) return census
    for (const name of (await fs.readDirectoryAsync(rootUri)).slice().sort()) {
      const child = await fs.getInfoAsync(rootUri + name)
      if (isStorageSafeId(name) && child.exists && child.isDirectory) {
        const state = await loadOrg(name)
        if (state.records.length === 0) continue
        census.organizations.push(name)
        for (const record of state.records) {
          if (record.status === 'ok' && record.entry.installId !== installId) {
            census.foreignInstallEntries.push({
              orgFirestoreId: name,
              entryId: record.entry.entryId,
              seq: record.entry.seq,
              installId: record.entry.installId,
            })
          }
        }
        continue
      }
      census.unattributed.push(
        strayRecord(name, 'not an organization directory', await readIfFile(rootUri + name)).slot,
      )
    }
    return census
  }

  return store
}

// ============================================================================
// Platform binding + disk-space helper
// ============================================================================

/** The platform store for one namespace: its directory under Documents, its install-id file under Caches. */
function platformStoreFor(rootDirName: string, installIdFileName: string): () => OutboxStore {
  return () => {
    const { documentDirectory, cacheDirectory } = ExpoFileSystem
    if (!documentDirectory) throw new Error('expo-file-system documentDirectory is unavailable')
    if (!cacheDirectory) throw new Error('expo-file-system cacheDirectory is unavailable')
    return createNativeOutboxStore({
      fs: ExpoFileSystem,
      rootUri: `${documentDirectory}${rootDirName}/`,
      installIdUri: `${cacheDirectory}${installIdFileName}`,
      vendorId: platformVendorId,
      installationTime: platformInstallationTime,
    })
  }
}

const sharedStore = createSharedStoreOpener(platformStoreFor(OUTBOX_ROOT_DIR_NAME, OUTBOX_INSTALL_ID_FILE_NAME))
const sharedMirrorIntentStore = createSharedStoreOpener(
  platformStoreFor(MIRROR_INTENT_ROOT_DIR_NAME, MIRROR_INTENT_INSTALL_ID_FILE_NAME),
)
const sharedQuarantineStore = createSharedStoreOpener(platformStoreFor(QUARANTINE_ROOT_DIR_NAME, QUARANTINE_INSTALL_ID_FILE_NAME))

/**
 * The app's one native outbox store (see ONE INSTANCE PER DIRECTORY above). After `close()` the
 * next call returns a fresh one that waits for the old one to finish (review 2, item 1). Callers
 * must NOT keep the returned store across a `close()` — call `openOutboxStore()` again; a kept
 * reference is the closed instance and refuses every operation.
 */
export function openOutboxStore(): OutboxStore {
  return sharedStore.open()
}

/**
 * The app's one native MIRROR-INTENT store (ATL-1459): same engine and contract as
 * `openOutboxStore`, its own directory (`MIRROR_INTENT_ROOT_DIR_NAME`). Same rule: never keep the
 * returned store across a `close()`.
 */
export function openMirrorIntentStore(): OutboxStore {
  return sharedMirrorIntentStore.open()
}

/**
 * The app's one native QUARANTINE store (P8 review 1, S-3): same engine and contract, its own directory
 * (`QUARANTINE_ROOT_DIR_NAME`), never drained. Same rule: never keep it across a `close()`.
 */
export function openQuarantineStore(): OutboxStore {
  return sharedQuarantineStore.open()
}

export type FreeDiskCheck =
  | { status: 'ok' | 'low'; freeBytes: number; thresholdBytes: number }
  | { status: 'unknown'; freeBytes: null; thresholdBytes: number; error: string }

/**
 * Free-space check for boot (plan §2 "check getFreeDiskStorageAsync at boot and warn"). Reports;
 * never throws, never decides — the caller warns. `unknown` is its own answer, not `ok`.
 */
export async function checkOutboxFreeDisk(
  fs: Pick<OutboxFileSystem, 'getFreeDiskStorageAsync'> = ExpoFileSystem,
  thresholdBytes: number = LOW_DISK_BYTES,
): Promise<FreeDiskCheck> {
  try {
    const freeBytes = await fs.getFreeDiskStorageAsync()
    if (typeof freeBytes !== 'number' || !Number.isFinite(freeBytes) || freeBytes < 0) {
      return { status: 'unknown', freeBytes: null, thresholdBytes, error: `unusable reading: ${String(freeBytes)}` }
    }
    return { status: freeBytes < thresholdBytes ? 'low' : 'ok', freeBytes, thresholdBytes }
  } catch (error) {
    return {
      status: 'unknown',
      freeBytes: null,
      thresholdBytes,
      error: error instanceof Error ? error.message : String(error),
    }
  }
}
